import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup, Button, Card, ListGroup, Row, Col, Form } from 'react-bootstrap';
import { withTracker } from 'meteor/react-meteor-data';
import _, { clone, concat, get, isEmpty, isEqual, isNil } from 'lodash';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';

import * as layerOps from '../../../api/Layers/layer-operations';
import * as mapOps from '../../../api/Maps/map-operations';
import Maps from '../../../api/Maps/Maps';
import Layers from '../../../api/Layers/Layers';
import { getDefaultBaseMap } from '../../../api/Users/user-operations';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

import LayersInMapList from '../../atoms/LayersInMapList';
import PageHeader from '../../atoms/PageHeader';
import ColorPicker from '../../components/ColorPicker';
import EsriWebMap from '../../components/EsriWebMap';
import Loading from '../../components/Loading';
import MapShareComponent from '../../components/MapShareComponent';
import CesiumViewerContainerViewMap from '../../containers/CesiumViewerContainerViewMap';
import MapContainerViewMap from '../../containers/MapContainerViewMap';
import NotFound from '../NotFound';
import MapSharePrivateComponent from '../../components/MapSharePrivateComponent';
import MapShareUserPrivateComponent from '../../components/MapShareUserPrivateComponent';
import MapShareUserPublicComponent from '../../components/MapShareUserPublicComponent';

const { isAdvanced } = Meteor.settings.public;

const moveItem = (array, from, to) => {
  const arrayToMutate = clone(array);
  // remove `from` item and store it
  const f = arrayToMutate.splice(from, 1)[0];
  // insert stored item into position `to`
  arrayToMutate.splice(to, 0, f);
  return arrayToMutate;
};

class ViewMap extends Component {
  state = {
    layerIdsArray: get(this.props, 'map.layers', []),
    baseMapIdsArray: get(this.props, 'map.baseMaps', []),
    updatingLayerOrder: true,
    location: {
      lat: get(this.props, 'map.mapLocation.geometry.coordinates[1]', []),
      lng: get(this.props, 'map.mapLocation.geometry.coordinates[0]', []),
      zoom: get(this.props, 'map.mapLocation.properties.zoom', [])
    }
  };

  componentDidUpdate(prevProps) {
    const { map: oldMap } = prevProps;
    const { map } = this.props;
    if (!isEmpty(map) && !isEqual(mapOps.getLayers(map), mapOps.getLayers(oldMap))) {
      this.updateLayers(mapOps.getLayers(map));
    }
    if (!isEmpty(map) && !isEqual(mapOps.getBaseMaps(map), mapOps.getBaseMaps(oldMap))) {
      this.updateBaseMaps(mapOps.getBaseMaps(map));
    }
    if (!isEmpty(map) && !isEqual(mapOps.getMapLocation(map), mapOps.getMapLocation(oldMap))) {
      const newLocation = {
        lat: get(this.props, 'map.mapLocation.geometry.coordinates[1]', []),
        lng: get(this.props, 'map.mapLocation.geometry.coordinates[0]', []),
        zoom: get(this.props, 'map.mapLocation.properties.zoom', [])
      };
      this.updateLocation(newLocation);
    }
  }

  updateLayers = newLayers => this.setState({ layerIdsArray: newLayers });

  updateBaseMaps = newBaseMaps => this.setState({ baseMapIdsArray: newBaseMaps });

  handleRemove = async (mapId, history) => {
    try {
      await methodPromise('maps.softDelete', mapId);
      Bert.alert('Map deleted!', 'warning');
      history.push(`/maps`);
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  saveLayerOrder = async newLayerOrder => {
    const { map } = this.props;
    const mapId = mapOps.getId(map);
    try {
      await methodPromise('maps.saveNewLayerOrder', mapId, newLayerOrder);
      Bert.alert('Layer Order Updated', 'success');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  saveBaseMapOrder = async newBaseMapOrder => {
    const { map } = this.props;
    const mapId = mapOps.getId(map);
    try {
      await methodPromise('maps.saveNewBaseMapOrderMap', mapId, newBaseMapOrder);
      Bert.alert('Layer Order Updated', 'success');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  selectMainBaseMap = baseMapId => {
    const { baseMapIdsArray: currentBaseMapOrder, updatingLayerOrder } = this.state;
    if (updatingLayerOrder) {
      Bert.alert('Layer Loading', 'danger');
      return false;
    }
    this.setState({ updatingLayerOrder: true });
    const baseMapIndex = currentBaseMapOrder.indexOf(baseMapId);
    if (baseMapIndex === 0) {
      this.setState({ updatingLayerOrder: false });
      return false;
    }
    const movedArray = moveItem(currentBaseMapOrder, baseMapIndex, 0);
    this.saveBaseMapOrder(movedArray);
    return true;
  };

  moveLayerUp = layerId => {
    const { layerIdsArray: currentLayerOrder, updatingLayerOrder } = this.state;
    if (updatingLayerOrder) {
      Bert.alert('Layer Loading', 'danger');
      return false;
    }
    this.setState({ updatingLayerOrder: true });
    const layerIndex = currentLayerOrder.indexOf(layerId);
    if (layerIndex === 0) {
      this.setState({ updatingLayerOrder: false });
      return false;
    }
    const movedArray = moveItem(currentLayerOrder, layerIndex, layerIndex - 1);
    this.saveLayerOrder(movedArray);
    return true;
  };

  moveLayerDown = layerId => {
    const { layerIdsArray: currentLayerOrder, updatingLayerOrder } = this.state;
    if (updatingLayerOrder) {
      Bert.alert('Layer Loading', 'danger');
      return false;
    }
    this.setState({ updatingLayerOrder: true });
    const layerIndex = currentLayerOrder.indexOf(layerId);
    if (layerIndex === currentLayerOrder.length - 1) {
      this.setState({ updatingLayerOrder: false });
      return false;
    }
    const movedArray = moveItem(currentLayerOrder, layerIndex, layerIndex + 1);
    this.saveLayerOrder(movedArray);
    return true;
  };

  toggleShared = async (mapId, isShared) => {
    try {
      await methodPromise('maps.toggleShared', mapId, !isShared);
      if (!isShared) {
        Bert.alert('Link Created', 'success');
      } else {
        Bert.alert('No longer Shared', 'info');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  toggleSharedUser = async (mapId, isShared) => {
    try {
      await methodPromise('maps.toggleSharedUser', mapId, !isShared);
      if (!isShared) {
        Bert.alert('Link Created', 'success');
      } else {
        Bert.alert('No longer Shared', 'info');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  toggleWithLocation = async (mapId, isWithLocation) => {
    try {
      await methodPromise('maps.toggleWithLocation', mapId, !isWithLocation);
      if (!isWithLocation) {
        Bert.alert('Location Created', 'success');
      } else {
        Bert.alert('No longer Location', 'info');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  toggleWithLocationPrivate = async (mapId, isWithLocationPrivate) => {
    try {
      await methodPromise('maps.toggleWithLocationPrivate', mapId, !isWithLocationPrivate);
      if (!isWithLocationPrivate) {
        Bert.alert('Location Created', 'success');
      } else {
        Bert.alert('No longer Location', 'info');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  toggleWithLocationUser = async (mapId, isWithLocationUser) => {
    try {
      await methodPromise('maps.toggleWithLocationUser', mapId, !isWithLocationUser);
      if (!isWithLocationUser) {
        Bert.alert('Location Private Created', 'success');
      } else {
        Bert.alert('No longer Location Private', 'info');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  toggleSharedUserPrivate = async (mapId, isShared) => {
    try {
      await methodPromise('maps.toggleSharedUserPrivate', mapId, !isShared);
      if (!isShared) {
        Bert.alert('Link Created', 'success');
      } else {
        Bert.alert('No longer Shared', 'info');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  toggleWithLocationUserPrivate = async (mapId, isWithLocationUserPrivate) => {
    try {
      await methodPromise('maps.toggleWithLocationUserPrivate', mapId, !isWithLocationUserPrivate);
      if (!isWithLocationUserPrivate) {
        Bert.alert('Location Private Created', 'success');
      } else {
        Bert.alert('No longer Location Private', 'info');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  toggleSharedPrivate = async (mapId, isShared) => {
    try {
      await methodPromise('maps.toggleSharedPrivate', mapId, !isShared);
      if (!isShared) {
        Bert.alert('Link Created', 'success');
      } else {
        Bert.alert('No longer Shared', 'info');
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  updateLocation = newState => this.setState({ location: newState });

  updateLayerMapState = newState => this.setState({ updatingLayerOrder: newState });

  changeBackgroundColor = newBackgroundMapColor => {
    const { map } = this.props;
    methodPromise('maps.updateBackgroundColor', mapOps.getId(map), newBackgroundMapColor.hex);
  };

  render() {
    const {
      deployEnv,
      history,
      isFree,
      layers,
      loading,
      map,
      match,
      userControlSetting
    } = this.props;
    const { location } = this.state;
    if (loading) return <Loading />;
    if (isEmpty(map) || mapOps.isDeleted(map)) return <NotFound />;

    const layerItems = mapOps.getLayerObjectsArray(map, layers);
    const baseMap = mapOps.getLayerObjectsArray(map, layers, false);
    const baseMapDefault =
      userControlSetting.defaultBaseMap && !isEmpty(userControlSetting.defaultBaseMap)
        ? layers.find(layer => layer._id === userControlSetting.defaultBaseMap)
        : '';
    const baseMapItems = isEmpty(baseMap) ? [baseMapDefault] : baseMap;
    if (isEmpty(map) || mapOps.isDeleted(map)) return <NotFound />;

    return (
      <>
        <PageHeader>
          <h4 className="mr-auto">{mapOps.getName(map)}</h4>
          <ButtonGroup size="sm">
            <Button variant="outline-info" onClick={() => history.push(`${match.url}/edit`)}>
              Editar
            </Button>
            {(mapOps.isLeafletMap(map) || mapOps.isIASMap(map)) &&
              (mapOps.areFeature(map) && isAdvanced ? (
                <>
                  <Button
                    variant="outline-info"
                    onClick={() => history.push(`${match.url}/AdvancedMap`)}
                  >
                    Mapa avanzado
                  </Button>
                </>
              ) : null)}
            <Button
              onClick={() => this.handleRemove(mapOps.getId(map), history)}
              variant="outline-danger"
            >
              Eliminar
            </Button>
          </ButtonGroup>
        </PageHeader>
        <Row noGutters>
          {mapOps.isEsriMap(map) && (
            <Col xs={12}>
              <EsriWebMap
                itemId={mapOps.getEsriWebMapId(map)}
                mapCenter={mapOps.getMapLocation(map)}
              />
            </Col>
          )}
          {!mapOps.isEsriMap(map) && (
            <>
              <Col xs={12} sm={4} md={3} className="pr-1">
                {!isFree && (
                  <>
                    <MapShareComponent
                      map={map}
                      toggleShared={this.toggleShared}
                      toggleWithLocation={this.toggleWithLocation}
                      location={location}
                    />
                    <br />
                    <MapSharePrivateComponent
                      map={map}
                      toggleSharedPrivate={this.toggleSharedPrivate}
                      toggleWithLocationPrivate={this.toggleWithLocationPrivate}
                      location={location}
                    />
                    <br />
                    <MapShareUserPrivateComponent
                      map={map}
                      toggleSharedUserPrivate={this.toggleSharedUserPrivate}
                      toggleWithLocationUserPrivate={this.toggleWithLocationUserPrivate}
                      location={location}
                    />
                    <br />
                    <MapShareUserPublicComponent
                      map={map}
                      toggleSharedUser={this.toggleSharedUser}
                      toggleWithLocationUser={this.toggleWithLocationUser}
                      location={location}
                    />
                    <br />
                  </>
                )}

                <Card>
                  <Card.Header>
                    <Form.Group as={Row}>
                      <Form.Label column xs={8}>
                        Background
                      </Form.Label>
                      <Col xs={4}>
                        <ColorPicker
                          color={mapOps.getBackground(map)}
                          alpha={1}
                          updateColor={this.changeBackgroundColor}
                          disableAlpha
                        />
                      </Col>
                    </Form.Group>
                  </Card.Header>
                </Card>
                <br />
                <Card>
                  <Card.Header>Lista de layer</Card.Header>
                  <ListGroup>
                    {_.map(layerItems, layerItem => (
                      <LayersInMapList
                        key={layerOps.getId(layerItem)}
                        moveLayerUp={this.moveLayerUp}
                        moveLayerDown={this.moveLayerDown}
                        isLayer
                        layerItem={layerItem}
                        map={map}
                      />
                    ))}
                  </ListGroup>
                </Card>
                <br />
                <Card>
                  <Card.Header>Lista de mapa base</Card.Header>
                  <ListGroup>
                    {_.map(baseMapItems, (baseMapItem, index) => (
                      <LayersInMapList
                        key={layerOps.getId(baseMapItem)}
                        layerItem={baseMapItem}
                        map={map}
                        isBaseMap
                        isDefaultBaseMap={index === 0}
                        makeDefaultBaseMap={this.selectMainBaseMap}
                      />
                    ))}
                  </ListGroup>
                </Card>
              </Col>
              <Col xs={12} sm={8} md={9}>
                {(mapOps.isLeafletMap(map) || mapOps.isIASMap(map)) && (
                  <MapContainerViewMap
                    baseMaps={baseMapItems}
                    deployEnv={deployEnv}
                    layers={layerItems}
                    map={map}
                    updateLayerMapState={this.updateLayerMapState}
                    updateLocation={this.updateLocation}
                    userControlSetting={userControlSetting}
                  />
                )}
                {mapOps.isCesiumMap(map) && (
                  <CesiumViewerContainerViewMap
                    baseMaps={baseMapItems}
                    layers={layerItems}
                    map={map}
                    updateLayerMapState={this.updateLayerMapState}
                    userControlSetting={userControlSetting}
                  />
                )}
              </Col>
            </>
          )}
        </Row>
      </>
    );
  }
}

ViewMap.defaultProps = {
  map: {},
  layers: []
};

ViewMap.propTypes = {
  loading: PropTypes.bool.isRequired,
  isFree: PropTypes.bool.isRequired,
  map: PropTypes.object,
  layers: PropTypes.array,
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  deployEnv: PropTypes.string.isRequired,
  userControlSetting: PropTypes.object.isRequired
};

export default withTracker(({ match }) => {
  const mapId = match.params.map_id;
  const mapSub = Meteor.subscribe('maps.view', mapId);
  const map = Maps.findOne(mapId);

  let layerIds = [];
  let layersAndBaseMaps = [];
  if (!isNil(map)) {
    layersAndBaseMaps = concat(mapOps.getLayers(map), mapOps.getBaseMaps(map));
    layerIds = concat(getDefaultBaseMap(Meteor.user()), layersAndBaseMaps);
  }
  const layersSub = Meteor.subscribe('layers.viewMap', layerIds);

  return {
    loading: !mapSub.ready() || !layersSub.ready(),
    map,
    layers: Layers.find({ _id: { $in: layerIds }, deleted: { $eq: 'no' } }).fetch()
  };
})(ViewMap);
